import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';

interface UseCircuitBenefitsStateProps {
  index: number;
  stepIndex: number;
  control: Control<CircuitFormDataWithTranslations>;
}

type BenefitType = 'transport' | 'activities' | 'meals' | 'accommodations';

export const useCircuitBenefitsState = ({
  index,
  stepIndex,
  control,
}: UseCircuitBenefitsStateProps) => {
  const stepPath = `circuitDays.${index}.steps.${stepIndex}` as const;
  const benefits = useWatch({
    control,
    name: stepPath,
  });

  // Memoized computed values
  const atLeastOneStandard = useMemo(
    () =>
      Boolean(
        benefits.accommodations?.standardBenefit ??
        benefits.meals?.standardBenefit ??
        benefits.activities?.standardBenefit ??
        benefits.transport?.standardBenefit,
      ),
    [benefits],
  );

  const optionalLength = useMemo(
    () =>
      (benefits.accommodations?.optionalBenefits?.length ?? 0) +
      (benefits.meals?.optionalBenefits?.length ?? 0) +
      (benefits.activities?.optionalBenefits?.length ?? 0) +
      (benefits.transport?.optionalBenefits?.length ?? 0),
    [benefits],
  );

  // Memoized benefit properties
  const benefitProperties = useMemo(
    () => ({
      transport: {
        indexStepDay: index - (benefits.transport?.indexFirstDay ?? 0),
        stepNbDays: benefits.transport?.nbDays ?? 1,
        standardDisplays: benefits.transport?.standardDisplays,
        optionalDisplays: benefits.transport?.optionalDisplays,
        time: benefits.transport?.time?.value,
        id: benefits.transport?.id,
      },
      activities: {
        indexStepDay: index - (benefits.activities?.indexFirstDay ?? 0),
        stepNbDays: benefits.activities?.nbDays ?? 1,
        standardDisplays: benefits.activities?.standardDisplays,
        optionalDisplays: benefits.activities?.optionalDisplays,
        time: benefits.activities?.time?.value,
        id: benefits.activities?.id,
      },
      meals: {
        indexStepDay: index - (benefits.meals?.indexFirstDay ?? 0),
        stepNbDays: benefits.meals?.nbDays ?? 1,
        standardDisplays: benefits.meals?.standardDisplays,
        optionalDisplays: benefits.meals?.optionalDisplays,
        time: benefits.meals?.time?.value,
        id: benefits.meals?.id,
      },
      accommodations: {
        indexStepDay: index - (benefits.accommodations?.indexFirstDay ?? 0),
        stepNbDays: benefits.accommodations?.nbDays ?? 1,
        standardDisplays: benefits.accommodations?.standardDisplays,
        optionalDisplays: benefits.accommodations?.optionalDisplays,
        time: benefits.accommodations?.time?.value,
        id: benefits.accommodations?.id,
        standardPensionType: benefits.accommodations?.standardBenefitPensionType,
        optionalPensionsTypes:
          benefits.accommodations?.optionalBenefitsPensionTypes ?? {},
      },
    }),
    [benefits, index],
  );

  // Helper function to get optional benefits by type
  const getOptionalBenefits = (type: BenefitType) => {
    return benefits[type]?.optionalBenefits ?? [];
  };

  return {
    benefits,
    atLeastOneStandard,
    optionalLength,
    benefitProperties,
    optionalBenefits: {
      transport: {
        benefits: getOptionalBenefits('transport'),
      },
      activities: {
        benefits: getOptionalBenefits('activities'),
      },
      meals: {
        benefits: getOptionalBenefits('meals'),
      },
      accommodations: {
        benefits: getOptionalBenefits('accommodations'),
      },
    },
  };
};
